import { useState, type FormEvent } from 'react';
import { useTenants } from '../../lib/api/hooks.js';
import { useCreateTenant } from '../../lib/api/hooks-billing.js';
import { StatusBadge, TableSkeleton, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { ApiClientError } from '../../lib/api/client.js';

export function TenantsPage() {
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const can = useSession((s) => s.can);
  const setActiveTenant = useSession((s) => s.setActiveTenant);
  const activeTenantId = useSession((s) => s.activeTenantId);
  const { data, isLoading, error } = useTenants({ q: q || undefined, limit: 50 });

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Tenants</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Customer accounts. Select one to manage its extensions, calls and billing.
          </p>
        </div>
        {can('tenant:create') && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ New tenant'}
          </button>
        )}
      </div>

      {error && <ErrorBanner error={error} />}
      {adding && <NewTenantForm onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <input className="arc-input" placeholder="Search by name or slug…" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Search tenants" />
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {data ? `${data.data.length} shown` : ''}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table arc-table-clickable">
            <thead>
              <tr>
                <th>Name</th><th>SIP domain</th><th>Status</th><th>Timezone</th>
                <th className="arc-td-right">Max calls</th><th className="arc-td-right">CPS</th><th />
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={7} /> : (
              <tbody>
                {data?.data.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setActiveTenant(t.id)}
                    style={t.id === activeTenantId ? { background: 'var(--arc-accent-subtle)' } : undefined}
                  >
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 570 }}>{t.name}</span>
                        <span className="arc-subtle" style={{ fontSize: 12 }}>{t.slug}</span>
                      </div>
                    </td>
                    <td className="arc-mono arc-subtle">{t.sip_domain}</td>
                    <td><StatusBadge status={t.status} /></td>
                    <td className="arc-muted">{t.timezone}</td>
                    <td className="arc-td-right arc-num">{t.max_concurrent_calls}</td>
                    <td className="arc-td-right arc-num">{t.max_cps}</td>
                    <td className="arc-td-right">
                      {t.id === activeTenantId
                        ? <span className="arc-badge arc-badge-info">selected</span>
                        : <span className="arc-subtle" style={{ fontSize: 12 }}>select</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!isLoading && data?.data.length === 0 && (
          <Empty icon="⬢" title="No tenants found"
            hint={q ? 'Try a different search term.' : 'Create your first tenant to get started.'} />
        )}
      </div>
    </>
  );
}

function NewTenantForm({ onDone }: { onDone: () => void }) {
  const create = useCreateTenant();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);

  // Derive slug and SIP domain from the name until the operator edits them.
  const onName = (v: string) => {
    setName(v);
    if (!touchedSlug) {
      const s = v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
      setSlug(s);
      setDomain(s ? `${s}.arcnet.arcline.dev` : '');
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      { name, slug, sip_domain: domain, timezone: 'Africa/Johannesburg', country_code: 'ZA', currency: 'ZAR' },
      { onSuccess: () => { setName(''); setSlug(''); setDomain(''); onDone(); } },
    );
  };

  const err = create.error instanceof ApiClientError ? create.error : null;
  const fieldErrors = err?.fieldErrors ?? {};

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 16 }} onSubmit={submit}>
      <h2 className="arc-h2" style={{ marginBottom: 14 }}>New tenant</h2>

      {err && Object.keys(fieldErrors).length === 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }} role="alert">
          <span aria-hidden="true">⚠</span><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="t-name">Company name</label>
          <input id="t-name" className="arc-input" required value={name}
            onChange={(e) => onName(e.target.value)} placeholder="Meridian Logistics" />
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="t-slug">Slug</label>
          <input id="t-slug" className="arc-input" required value={slug}
            onChange={(e) => { setTouchedSlug(true); setSlug(e.target.value); }}
            aria-invalid={!!fieldErrors['slug']} />
          {fieldErrors['slug'] && <div className="arc-error">{fieldErrors['slug']}</div>}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="t-domain">SIP domain</label>
          <input id="t-domain" className="arc-input" required value={domain}
            onChange={(e) => setDomain(e.target.value)}
            aria-invalid={!!fieldErrors['sip_domain']} />
          {/*
            ADR-002/ADR-013: HA1 credentials are bound to the realm, so changing
            this later de-registers every handset on the tenant. The database
            refuses the UPDATE outright; saying so here avoids the mistake.
          */}
          <div className="arc-hint" style={{ color: 'var(--arc-warn)' }}>
            Permanent — cannot be changed after creation.
          </div>
          {fieldErrors['sip_domain'] && <div className="arc-error">{fieldErrors['sip_domain']}</div>}
        </div>

        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </form>
  );
}
